import { useEffect, useMemo, useState } from 'react'
import { Plus, X } from 'lucide-react'
import {
  api, errorMessage,
  type DictItem, type DictKey, type DictCreateInput, type DictPatchInput,
} from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/Table'
import { useToast } from '@/components/ui/Toast'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { useI18n, type TKey } from '@/lib/i18n'
import { cn } from '@/lib/cn'
import { PageError, PageLoading } from './Stats'

// The dict_items table is generic (any dict_key is allowed by the API), but
// the admin UI pins three tabs — the only keys product code reads today.
// Adding a fourth tab is a one-liner here once a use case shows up.
const TABS: { key: DictKey; labelKey: TKey }[] = [
  { key: 'variant', labelKey: 'dict.tab.variant' },
  { key: 'cat',     labelKey: 'dict.tab.cat' },
  { key: 'brand',   labelKey: 'dict.tab.brand' },
]

interface Editing {
  id?: string
  dictKey: DictKey
  code: string
  labelZh: string
  labelEn: string
  sortOrder: number
  active: 0 | 1
}

function blankRow(dictKey: DictKey): Editing {
  return { dictKey, code: '', labelZh: '', labelEn: '', sortOrder: 0, active: 1 }
}

function toEditing(r: DictItem): Editing {
  return {
    id: r.id,
    dictKey: r.dictKey as DictKey,
    code: r.code,
    labelZh: r.labelZh,
    labelEn: r.labelEn ?? '',
    sortOrder: r.sortOrder,
    active: r.active === 1 ? 1 : 0,
  }
}

export function DictionariesScreen() {
  const toast = useToast()
  const { t } = useI18n()
  const [tab, setTab] = useState<DictKey>('variant')
  const [rows, setRows] = useState<DictItem[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<Editing | null>(null)
  const [deleting, setDeleting] = useState<DictItem | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  function load() {
    setBusy(true); setError(null)
    api.listDict(tab)
      .then((r) => setRows(r.items))
      .catch((e) => setError(errorMessage(e)))
      .finally(() => setBusy(false))
  }
  useEffect(() => { load() }, [tab])

  async function save(form: Editing) {
    try {
      if (form.id) {
        const patch: DictPatchInput = {
          code: form.code,
          labelZh: form.labelZh,
          labelEn: form.labelEn || null,
          sortOrder: form.sortOrder,
          active: form.active,
        }
        await api.patchDict(form.id, patch)
        toast.success(form.code, t('dict.toast.updated'))
      } else {
        const create: DictCreateInput = {
          dictKey: form.dictKey,
          code: form.code,
          labelZh: form.labelZh,
          labelEn: form.labelEn || null,
          sortOrder: form.sortOrder,
          active: form.active,
        }
        await api.createDict(create)
        toast.success(form.code, t('dict.toast.created'))
      }
      setEditing(null)
      load()
    } catch (e) {
      toast.error(t('dict.toast.saveFailed'), errorMessage(e))
    }
  }

  async function confirmDelete() {
    if (!deleting) return
    setDeleteBusy(true)
    try {
      await api.deactivateDict(deleting.id)
      toast.success(deleting.code, t('dict.toast.deactivated'))
      setDeleting(null)
      load()
    } catch (e) {
      toast.error(t('dict.toast.deleteFailed'), errorMessage(e))
    } finally { setDeleteBusy(false) }
  }

  if (error) return <PageError msg={error}/>
  if (!rows && busy) return <PageLoading/>

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t('dict.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('dict.subtitle', { n: rows?.length ?? 0 })}</p>
        </div>
        <Button onClick={() => setEditing(blankRow(tab))}>
          <Plus size={14} strokeWidth={2}/> {t('dict.action.new')}
        </Button>
      </header>

      <div className="flex items-center gap-1 border-b border-border">
        {TABS.map((tb) => (
          <button
            key={tb.key}
            type="button"
            onClick={() => setTab(tb.key)}
            className={cn(
              'px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
              tab === tb.key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t(tb.labelKey)}
          </button>
        ))}
      </div>

      <Table>
        <THead>
          <Tr>
            <Th>{t('dict.col.code')}</Th>
            <Th>{t('dict.col.labelZh')}</Th>
            <Th>{t('dict.col.labelEn')}</Th>
            <Th className="w-20 text-right pr-4">{t('dict.col.sortOrder')}</Th>
            <Th className="w-20">{t('dict.col.active')}</Th>
            <Th className="text-right pr-4">{t('dict.col.actions')}</Th>
          </Tr>
        </THead>
        <TBody>
          {rows && rows.length === 0 && (
            <Tr><Td colSpan={6} className="text-center text-muted-foreground py-8">{t('dict.empty')}</Td></Tr>
          )}
          {rows?.map((r) => (
            <Tr key={r.id}>
              <Td className="font-mono text-xs">{r.code}</Td>
              <Td className="font-medium">{r.labelZh}</Td>
              <Td className="text-muted-foreground">{r.labelEn ?? '—'}</Td>
              <Td className="text-right pr-4 tabular-nums text-muted-foreground">{r.sortOrder}</Td>
              <Td>
                {r.active === 1
                  ? <Badge tone="success">on</Badge>
                  : <Badge>off</Badge>}
              </Td>
              <Td className="text-right space-x-1.5 whitespace-nowrap">
                <Button size="sm" variant="ghost" onClick={() => setEditing(toEditing(r))}>{t('action.edit')}</Button>
                {r.active === 1 && (
                  <Button size="sm" variant="danger" onClick={() => setDeleting(r)}>{t('action.delete')}</Button>
                )}
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>

      {editing && (
        <DictEditor
          value={editing}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={() => save(editing)}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        title={t('dict.confirmDelete.title')}
        message={deleting ? t('dict.confirmDelete.body', { code: deleting.code }) : ''}
        confirmLabel={t('dict.confirmDelete.confirm')}
        cancelLabel={t('dict.confirmDelete.cancel')}
        tone="danger"
        busy={deleteBusy}
        onCancel={() => setDeleting(null)}
        onConfirm={confirmDelete}
      />
    </div>
  )
}

function DictEditor({
  value, onChange, onCancel, onSave,
}: {
  value: Editing
  onChange: (v: Editing) => void
  onCancel: () => void
  onSave: () => void
}) {
  const { t } = useI18n()
  const isEdit = !!value.id
  const canSave = useMemo(
    () => /^[a-z0-9][a-z0-9_-]*$/.test(value.code) && value.labelZh.trim().length > 0,
    [value.code, value.labelZh],
  )

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel() }}
    >
      <div className="w-full max-w-md rounded-lg bg-card border border-border shadow-xl">
        <div className="flex items-center justify-between border-b border-border px-5 py-3">
          <div className="font-semibold">
            {isEdit ? t('dict.editor.titleEdit') : t('dict.editor.titleNew')}
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-1 rounded hover:bg-muted text-muted-foreground"
            aria-label={t('dict.action.cancel')}
          >
            <X size={16}/>
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          <Field label={t('dict.field.code')} hint={t('dict.field.code.hint')}>
            <Input
              value={value.code}
              onChange={(e) => onChange({ ...value, code: e.target.value.trim().toLowerCase() })}
              placeholder="e.g. anthropic"
              autoFocus={!isEdit}
            />
          </Field>

          <Field label={t('dict.field.labelZh')}>
            <Input
              value={value.labelZh}
              onChange={(e) => onChange({ ...value, labelZh: e.target.value })}
              placeholder="例如：代充"
            />
          </Field>

          <Field label={t('dict.field.labelEn')}>
            <Input
              value={value.labelEn}
              onChange={(e) => onChange({ ...value, labelEn: e.target.value })}
              placeholder="e.g. Top-up"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label={t('dict.field.sortOrder')}>
              <Input
                type="number"
                value={String(value.sortOrder)}
                onChange={(e) => onChange({ ...value, sortOrder: Number(e.target.value) || 0 })}
              />
            </Field>
            <Field label={t('dict.field.active')}>
              <label className="flex items-center gap-2 h-9">
                <input
                  type="checkbox"
                  checked={value.active === 1}
                  onChange={(e) => onChange({ ...value, active: e.target.checked ? 1 : 0 })}
                />
                <span className="text-sm">{value.active === 1 ? 'on' : 'off'}</span>
              </label>
            </Field>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border px-5 py-3">
          <Button variant="ghost" onClick={onCancel}>{t('dict.action.cancel')}</Button>
          <Button onClick={onSave} disabled={!canSave}>{t('dict.action.save')}</Button>
        </div>
      </div>
    </div>
  )
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="text-xs font-medium text-muted-foreground mb-1">{label}</div>
      {children}
      {hint && <div className="text-[11px] text-muted-foreground mt-1">{hint}</div>}
    </label>
  )
}
